import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { icon, label, number, quote } from "../../../config";
import { addEmailAiPrompt, editAcknowledgementTemplate, editWorkflow, getEmailAiPrompt, updateEmailAiPrompt } from "../../../shared/services/workflow.services";
import DescriptionPopup from "../WorkflowBasicElements/DescriptionPopup";
import { useWorkflowContext } from "../WorkflowContextProvider";
import { workflowLabels, workflowQuote } from "../workflow.constants";
import { handleEditChange } from "../workflowHelper";
import "./../workflow.scss";
import "./emailTemplates.scss";

/**
 * Parent : EmailIntegration
 * This component displayes the email templates
 * @author Sarthak Arora
 */
const EmailTemplates = () => {
  const {
    workflow,
    workflowAckId,
    referenceEmail,
    setReferenceEmail,
    workflowAckEnabled,
    workflowAcknowledgementDesc,
    defaultSubject,
    setDefaultSubject,
    setWorkflowAcknowledgementDesc,
    taskAcknowledgementDesc,
    setTaskAcknowledgementDesc,
    setWorkflowAckEnabled,
    setAllowTaskFromEmail,
    allowTaskFromEmail,
    addTaskId,
    setAddTaskId,
    aiSummaryPrompt,
    setAiSummaryPrompt,
    savedAiSummaryPrompt,
    setSavedAiSummaryPrompt,
    aiSummaryEnabled,
    setAiSummaryEnabled,
    setShowDescPopup,
    showDescPopup,
  } = useWorkflowContext();
  const [previewText, setPreviewText] = useState("");
  const [workflowDescPopup, setWorkflowDescPopup] = useState(false);
  const [taskDescPopup, setTaskDescPopup] = useState(false);
  const subjectInputRef = useRef(null);
  const overlayRef = useRef(null);
  const { user } = useSelector((state) => state.auth);

  /**
   * sets the email subject
   * @author { Sejal Bansal}
   */
  useEffect(() => {
    let text = defaultSubject;

    const timestamp = new Date().getTime();
    text += `- #${timestamp}# `;
    setPreviewText(text);
  }, [defaultSubject, addTaskId]);

  /**
   * fetch and set the AI summary prompt for a given workflow if it is enabled
   * @author Muskan Thakur
   */
  useEffect(() => {
    const fetchEmailAiPrompt = async () => {
      const emailAiprompt = await getEmailAiPrompt({ workflowId: workflow?.Id, companyId: user?.companyId });
      setAiSummaryPrompt(emailAiprompt?.text ?? "");
      setSavedAiSummaryPrompt(emailAiprompt);
    };

    if (workflow?.Id && aiSummaryEnabled) {
      fetchEmailAiPrompt();
    }
  }, [aiSummaryEnabled]);

  /**
   * handler add reference Id
   * @author { Sarthak arora}
   */
  const handleTaskReference = async () => {
    setAddTaskId(true);

    const subjectInput = document.querySelector(".email-subject-integration");
    if (subjectInput) {
      const cursorPosition = subjectInput.selectionStart;
      const textBeforeCursor = defaultSubject.substring(0, cursorPosition);
      const textAfterCursor = defaultSubject.substring(cursorPosition);
      const newValue = `${textBeforeCursor}${label.PAX123}${textAfterCursor}`;

      setDefaultSubject(newValue);

      setTimeout(() => {
        const newCursorPosition = cursorPosition + label.PAX123.length;
        subjectInput.setSelectionRange(newCursorPosition, newCursorPosition);
        subjectInput.focus();
      }, 0);
    }

    editAcknowledgementTemplate({
      acknowledgementId: workflowAckId,
      AddTaskId: true,
      createdBy: user.id,
    });
  };

  /** function for handling when clicked save
   * on ackEmailContent
   * @author Pragun Gandotra
   */
  const handleSaveAckEmailContent = () => {
    editAcknowledgementTemplate({
      acknowledgementId: workflowAckId,
      template: workflowAcknowledgementDesc,
      createdBy: user.id,
    });
  };

  /**
   * handler for saving task email description
   * @author { Sejal Bansal}
   */
  const handleSaveReferenceContent = () => {
    editAcknowledgementTemplate({
      acknowledgementId: workflowAckId,
      taskAcknowledgeTemplate: taskAcknowledgementDesc,
      createdBy: user.id,
    });
  };

  /**
   * handler for saving email subject
   * @author { Sejal Bansal}
   */
  const saveSubjectChange = () => {
    editAcknowledgementTemplate({
      acknowledgementId: workflowAckId,
      defaultSubject: defaultSubject,
      createdBy: user.id,
    });
  };

  /**Shows the workflow description popup.
   * @author Bhavana
   */
  const handleShowDescPopup = () => {
    setShowDescPopup(true);
    setWorkflowDescPopup(true);
  };

  /**Shows the task description popup.
   * @author Bhavana
   */
  const handleShowTaskDescPopup = () => {
    setShowDescPopup(true);
    setTaskDescPopup(true);
  };

  /**Closes the workflow description popup.
   * @author Bhavana
   */
  const closeWorkflowDescriptionPopup = () => {
    setShowDescPopup(false);
    setWorkflowDescPopup(false);
  };

  /**Closes the task description popup.
   * @author Bhavana
   */
  const closeTaskDescriptionPopup = () => {
    setShowDescPopup(false);
    setTaskDescPopup(false);
  };

  /**
   * remoces reference tag on backspace click
   * @author { Sarthak Arora}
   */
  const handleSubjectKeyDown = (event) => {
    if (event.key === "Backspace" && subjectInputRef.current) {
      const input = subjectInputRef.current;
      const cursorPos = input.selectionStart;
      const tag = label.PAX123;

      if (cursorPos >= tag.length && defaultSubject.slice(cursorPos - tag.length, cursorPos) === tag) {
        const before = defaultSubject.slice(0, cursorPos - tag.length);
        const after = defaultSubject.slice(cursorPos);
        setDefaultSubject(before + after);

        setTimeout(() => {
          input.setSelectionRange(cursorPos - tag.length, cursorPos - tag.length);
        }, 0);

        event.preventDefault();
      }
    }
  };

  /**
   * syncs the overlay scroll
   * @author { Sarthak Arora}
   */
  const syncOverlayScroll = () => {
    if (subjectInputRef.current && overlayRef.current) {
      overlayRef.current.scrollLeft = subjectInputRef.current.scrollLeft;
    }
  };

  /**
   * renders the subject with tags
   * @author { Sarthak Arora}
   */
  const renderSubjectWithTags = () => {
    const parts = defaultSubject?.split(label.PAX123);
    const elements = [];
    for (let i = 0; i < parts?.length; i++) {
      if (parts[i]) elements?.push(<span key={`text-${i}`}>{parts[i]}</span>);
      if (i < parts?.length - 1) {
        elements.push(
          <span key={`tag-${i}`} className='reference-tag'>
            {label.REF_ID_TAG}
            <span
              className='remove'
              onClick={(e) => {
                e.stopPropagation();
                setDefaultSubject(defaultSubject.replace(label.PAX123, ""));
              }}>
              ×
            </span>
          </span>
        );
      }
    }
    return elements;
  };

  return (
    <div>
      <div className='flex-column '>
        {
          <div>
            <Tooltip parentTitle={true} position='bottom'>
              <div className=' d-flex flex-column mt-3'>
                <label className='text-black mb-0 '>{label.ACKNOWLEDGEMENT_EMAIL} </label>
                <div className='d-flex align-items-center'>
                  <span className='mb-0 cursor-pointer  edit-template-text ' onClick={handleShowDescPopup}>
                    {workflowLabels.MAIL_TEMPLATE}
                  </span>
                  <span title={label.EDIT} className='cursor-pointer edit-template-icon align-items-center mb-1 ml-1' onClick={handleShowDescPopup}>
                    {icon.EDIT_WORKSPACE}
                  </span>
                </div>
              </div>
            </Tooltip>
            <div className='mt-2 ml-4'>
              {workflowDescPopup && (
                <DescriptionPopup
                  desc={workflowAcknowledgementDesc}
                  taskKey={"AcknowledgeEmail"}
                  setDesc={setWorkflowAcknowledgementDesc}
                  workflow={workflow}
                  showDescPopup={showDescPopup}
                  setShowDescPopup={setShowDescPopup}
                  updateDescription={handleSaveAckEmailContent}
                  closeDescriptionPopup={closeWorkflowDescriptionPopup}
                  acknowledgeMailEditor={true}
                  headerLabel={workflowQuote.EDIT_ACKNOWLEDGEMENT_TEMPLATE}
                />
              )}
            </div>
          </div>
        }
      </div>
      <div className='flex-column d-flex'>
        {
          <div>
            <Tooltip parentTitle={true} position='bottom'>
              <div className=' d-flex flex-column'>
                <label className='m-0 text-black'>{label.TICKET_REFERENCE_EMAIL} </label>
                <div className='d-flex  align-items-center'>
                  <span className='mb-0 cursor-pointer edit-template-text ' onClick={handleShowTaskDescPopup}>
                    {workflowQuote.EDIT_SEND_TICKET_EMAIL_TEMPLATE}
                  </span>
                  <span id='workflow-save-email-btn' title={label.EDIT} className='cursor-pointer edit-template-icon align-items-center mb-1 ml-1' onClick={handleShowTaskDescPopup}>
                    {icon.EDIT_WORKSPACE}
                  </span>
                </div>
              </div>
            </Tooltip>
            <div className='mt-2 ml-4'>
              {taskDescPopup && (
                <DescriptionPopup
                  desc={taskAcknowledgementDesc}
                  setDesc={setTaskAcknowledgementDesc}
                  workflow={workflow}
                  taskKey={"TaskEmail"}
                  showDescPopup={showDescPopup}
                  setShowDescPopup={setShowDescPopup}
                  updateDescription={handleSaveReferenceContent}
                  closeDescriptionPopup={closeTaskDescriptionPopup}
                  acknowledgeMailEditor={true}
                  headerLabel={workflowQuote.EDIT_SEND_TICKET_EMAIL_TEMPLATE}
                />
              )}
            </div>

            {
              <div>
                <div className=' mt-3'>
                  <div className='form-class flex-column'>
                    <div>
                      <div className='d-flex justify-content-between reference-section w-100'>
                        <label className='mr-2 text-black'>{label.RESPONSE_EMAIL_SUBJECT_TEXT}</label>
                        <span className='pr-2 edit-template-text cursor-pointer ' onClick={handleTaskReference}>
                          {label.ADD_REFERENCE_ID}
                        </span>
                      </div>
                      <div className='custom-tag-input input-tag-container'>
                        {/* Overlay for tag rendering */}
                        <div className='input-tag-overlay' ref={overlayRef}>
                          <span className='input-tag-overlay-content'>{renderSubjectWithTags()}</span>
                        </div>
                        <input
                          ref={subjectInputRef}
                          type='text'
                          className='form-control email-subject-integration reference-input w-100'
                          value={defaultSubject}
                          onChange={(event) => setDefaultSubject(event.target.value)}
                          onBlur={saveSubjectChange}
                          onScroll={syncOverlayScroll}
                          onInput={syncOverlayScroll}
                          onKeyUp={syncOverlayScroll}
                          onClick={syncOverlayScroll}
                          onKeyDown={handleSubjectKeyDown}
                        />
                      </div>
                      <span className='pl-1 unique-id-info text-gray'>{quote.UNIQUE_ID_APPENDED}</span>
                    </div>
                    <div className='d-flex align-items-center mt-2'>
                      <div className=' flex-column'>
                        <label className='preview-text p-0 m-0 font-weight-normal'> Preview : {previewText}</label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            }
          </div>
        }
      </div>
    </div>
  );
};

export default EmailTemplates;
